import React from 'react';
import Icon from '../../../components/AppIcon';

const RiskAssessmentPanel = ({ project }) => {
  const bufferPoolData = {
    allocated: project?.bufferPool?.allocated || 15,
    available: project?.bufferPool?.available || 85,
    status: project?.bufferPool?.status || 'healthy'
  };

  const reversalRisks = [
    {
      category: 'Riesgo Climático',
      level: 'medio',
      score: 35,
      description: 'Eventos climáticos extremos potenciales',
      icon: 'CloudRain'
    },
    {
      category: 'Riesgo Social',
      level: 'bajo',
      score: 15,
      description: 'Conflictos comunitarios o cambios de uso de suelo',
      icon: 'Users'
    },
    {
      category: 'Riesgo Económico',
      level: 'bajo',
      score: 20,
      description: 'Presiones económicas para cambio de uso',
      icon: 'DollarSign'
    },
    {
      category: 'Riesgo Biológico',
      level: 'medio',
      score: 30,
      description: 'Plagas, enfermedades o incendios forestales',
      icon: 'Bug'
    }
  ];

  const leakageAlerts = [
    {
      type: 'Desplazamiento de Actividades',
      severity: 'bajo',
      location: 'Zona Norte',
      status: 'monitoreado',
      date: '2026-01-15',
      icon: 'MapPin'
    },
    {
      type: 'Cambio de Uso de Suelo',
      severity: 'medio',
      location: 'Zona Este',
      status: 'en_investigacion',
      date: '2026-01-10',
      icon: 'AlertTriangle'
    }
  ];

  const getRiskColor = (level) => {
    switch (level) {
      case 'bajo':
        return 'var(--color-success)';
      case 'medio':
        return 'var(--color-warning)';
      case 'alto':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'bajo':
        return 'var(--color-success)';
      case 'medio':
        return 'var(--color-warning)';
      case 'alto':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      monitoreado: 'Monitoreado',
      en_investigacion: 'En Investigación',
      resuelto: 'Resuelto',
      critico: 'Crítico'
    };
    return labels?.[status] || status;
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl font-semibold font-mono">Estado del Buffer Pool</h2>
        </div>

        <div className="p-6 bg-background rounded-lg border border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground caption mb-1">
                Créditos en Buffer Pool
              </p>
              <p className="text-3xl font-semibold text-foreground data-text">
                {bufferPoolData?.allocated}%
              </p>
            </div>
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                bufferPoolData?.status === 'healthy' ?'bg-success/10 text-success'
                  : bufferPoolData?.status === 'warning' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
              }`}
            >
              <Icon
                name={
                  bufferPoolData?.status === 'healthy' ?'CheckCircle'
                    : bufferPoolData?.status === 'warning' ?'AlertCircle' :'XCircle'
                }
                size={16}
              />
              <span className="text-sm font-medium caption">
                {bufferPoolData?.status === 'healthy' ?'Saludable'
                  : bufferPoolData?.status === 'warning' ?'Advertencia' :'Crítico'}
              </span>
            </div>
          </div>

          <div className="relative w-full h-4 bg-muted rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-primary transition-all duration-500"
              style={{ width: `${bufferPoolData?.allocated}%` }}
            />
          </div>

          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground caption">
            <span>Asignado: {bufferPoolData?.allocated}%</span>
            <span>Disponible: {bufferPoolData?.available}%</span>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-warning/10 rounded-lg">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          </div>
          <h2 className="text-xl font-semibold font-mono">Indicadores de Riesgo de Reversión</h2>
        </div>

        <div className="space-y-4">
          {reversalRisks?.map((risk, index) => (
            <div
              key={index}
              className="p-4 bg-background rounded-lg border border-border hover:border-primary/20 transition-all duration-250"
            >
              <div className="flex items-start gap-4">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: `${getRiskColor(risk?.level)}15` }}
                >
                  <Icon name={risk?.icon} size={20} color={getRiskColor(risk?.level)} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-medium text-foreground">{risk?.category}</h3>
                    <span
                      className="px-2 py-1 text-xs rounded caption font-medium"
                      style={{
                        backgroundColor: `${getRiskColor(risk?.level)}15`,
                        color: getRiskColor(risk?.level)
                      }}
                    >
                      {risk?.level?.charAt(0)?.toUpperCase() + risk?.level?.slice(1)}
                    </span>
                  </div>

                  <p className="text-xs text-muted-foreground caption mb-3">
                    {risk?.description}
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full transition-all duration-500"
                          style={{
                            width: `${risk?.score}%`,
                            backgroundColor: getRiskColor(risk?.level)
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground caption data-text">
                      {risk?.score}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
            <Icon name="AlertOctagon" size={20} color="var(--color-error)" />
          </div>
          <h2 className="text-xl font-semibold font-mono">Alertas de Fuga (Leakage)</h2>
        </div>

        {leakageAlerts?.length > 0 ? (
          <div className="space-y-3">
            {leakageAlerts?.map((alert, index) => (
              <div
                key={index}
                className="p-4 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-250"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: `${getSeverityColor(alert?.severity)}15` }}
                  >
                    <Icon name={alert?.icon} size={20} color={getSeverityColor(alert?.severity)} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-1">
                          {alert?.type}
                        </h3>
                        <p className="text-xs text-muted-foreground caption">
                          {alert?.location}
                        </p>
                      </div>
                      <span
                        className="px-2 py-1 text-xs rounded caption font-medium flex-shrink-0"
                        style={{
                          backgroundColor: `${getSeverityColor(alert?.severity)}15`,
                          color: getSeverityColor(alert?.severity)
                        }}
                      >
                        {alert?.severity?.charAt(0)?.toUpperCase() + alert?.severity?.slice(1)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4 mt-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground caption">
                          {new Date(alert.date)?.toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground caption">
                        {getStatusLabel(alert?.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto mb-4">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
            <p className="text-sm text-muted-foreground caption">
              No hay alertas de fuga activas en este momento
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessmentPanel;